<script setup>
import { Icon } from '@iconify/vue2'
import { onClickOutside } from '@vueuse/core'
import { computed, ref, toRefs, watch } from 'vue'
import Checkbox from '../ui/checkbox/Checkbox.vue'
import DropdownColunas from '../ui/baseTable/DropdownColunas'
import OfficialProcessingIcon from '../OfficialProcessingIcon/OfficialProcessingIcon'

const emit = defineEmits(['edit', 'update:checked', 'sortOptions'])

const props = defineProps({
	data: { type: Array, required: true },
	colunas: { type: Array, required: true },
	displayMode: { type: Boolean, default: false },
	checked: { type: Array, default: () => [] },
})

watch(
	() => props.checked,
	(value) => {
		const selectedData = props.data.filter((dado) => value.includes(dado.id))
		emit('update:checked', {
			selectedIds: value,
			selectedItems: selectedData,
		})
	},
)

const { data } = toRefs(props)
const checkAll = ref(false)
const quantityPerPage = ref(10)
const atualPage = ref(1)
const quantityPerPageOptions = ref([10, 20, 50])
const searchQuery = ref('')

const filteredData = computed(() => {
	if (!searchQuery.value) return data.value
	return data.value.filter((item) => {
		return Object.values(item).some((value) =>
			String(value).toLowerCase().includes(searchQuery.value.toLowerCase()),
		)
	})
})

const totalRecords = computed(() => filteredData.value.length)
const maxPages = computed(() => Math.ceil(totalRecords.value / quantityPerPage.value))

const paginatedData = computed(() => {
	const start = (atualPage.value - 1) * quantityPerPage.value
	const end = start + quantityPerPage.value
	return filteredData.value.slice(start, end)
})

function onSelectChange(event) {
	quantityPerPage.value = Number(event.target.value)
	atualPage.value = 1
}

function onPreviousPage() {
	if (atualPage.value > 1) atualPage.value--
}

function onNextPage() {
	if (atualPage.value < maxPages.value) atualPage.value++
}

function formatDate(date) {
	if (!date) return ''

	const parsedDate = new Date(date)
	if (isNaN(parsedDate)) return date // Retorna original se inválido

	const day = String(parsedDate.getDate()).padStart(2, '0')
	const month = String(parsedDate.getMonth() + 1).padStart(2, '0') // Mês começa do 0
	const year = parsedDate.getFullYear()

	return `${day}/${month}/${year}`
}

watch(checkAll, (value) => {
	if (value) {
		props.checked = filteredData.value.map((dado) => dado.id)
	} else {
		props.checked = []
	}
})

const allColumnsKeys = computed(() => {
	return props.colunas.map((d) => d.key)
})

const columnsEnabled = ref(allColumnsKeys.value)
const sortBy = ref([])
watch(sortBy, (value) => {
	emit('sortOptions', value)
})

function handleSort(key) {
	const index = sortBy.value.indexOf(key)
	if (index >= 0) {
		sortBy.value.splice(index, 1)
	} else {
		sortBy.value.push(key)
	}
}

const dropdownColunas = ref()
const openColunas = ref(false)

onClickOutside(dropdownColunas, () => {
	openColunas.value = false
})
</script>

<template>
	<div class="">
		<div
			v-if="paginatedData.length > 0"
			class="flex justify-between items-center mb-4"
			style="margin-top: 10px"
		>
			<div />
			<div class="flex items-center gap-4">
				<h2 class="text-xl font-semibold mb-4">
					{{ $t('processing') }}
				</h2>
			</div>
			<div class="relative">
				<input
					v-model="searchQuery"
					type="text"
					placeholder="Pesquisar"
					class="custom-border rounded-md px-3 py-1 w-64"
				/>
				<Icon
					icon="mdi:magnify"
					class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
				/>
			</div>
		</div>

		<!-- Container para a rolagem horizontal -->
		<div class="overflow-x-auto max-w-full">
			<table class="">
				<thead
					v-if="paginatedData.length > 0"
					class="bg-blue-tint-3 text-[#01395E]"
				>
					<tr>
						<th
							v-if="!displayMode"
							class="w-[2%] p-2 text-start"
						>
							<Checkbox v-model="checkAll" />
						</th>
						<th
							v-for="coluna in colunas.filter((f) => columnsEnabled.includes(f.key))"
							:key="coluna.key"
							class="p-2 text-start"
							:style="{ width: coluna.width }"
						>
							<div class="flex items-center justify-between">
								<p
									:title="coluna.nome"
									class="text-ellipsis overflow-hidden whitespace-nowrap max-w-min !m-0"
								>
									{{ coluna.nome }}
								</p>
								<Icon
									v-if="coluna.enableSort"
									icon="lucide:sort-desc"
									class="text-blue-tint-4 text-lg cursor-pointer"
									@click="handleSort(coluna.key)"
								/>
							</div>
						</th>
						<th class="p-2 text-start relative">
							<div
								v-if="openColunas"
								ref="dropdownColunas"
								class="absolute z-10 bg-white shadow-md p-2 rounded-md"
							>
								<DropdownColunas
									v-model="columnsEnabled"
									:options="colunas.map((d) => ({ label: d.nome, value: d.key }))"
								/>
							</div>
						</th>
					</tr>
				</thead>
				<tbody v-if="paginatedData.length > 0">
					<tr
						v-for="dado in paginatedData"
						:key="dado.id"
						:class="{
							'bg-blue-tint-4': checked.includes(dado.id),
							'even:bg-[#F1F5F8]': true,
						}"
					>
						<td
							v-if="!displayMode"
							class="p-2"
						>
							<Checkbox
								v-model="checked"
								:model-value="dado.id"
							/>
						</td>

						<td
							v-for="coluna in colunas.filter((f) => columnsEnabled.includes(f.key))"
							:key="coluna.key"
							:title="dado[coluna.key]"
							class="p-2 text-ellipsis overflow-hidden whitespace-nowrap text-center"
						>
							<div v-if="coluna.key === 'processamentoOficial'">
								<OfficialProcessingIcon
									:inventario-oficial="dado[coluna.key]"
									:is-list-view="true"
								/>
							</div>
							<div v-else-if="coluna.component">
								<Component
									:is="coluna.component"
									v-bind="{ ...dado, text: dado[coluna.key] }"
								/>
							</div>
							<!-- Se for uma data, formata -->
							<span v-else-if="coluna.tipo === 'date'">
								{{ formatDate(dado[coluna.key]) }}
							</span>

							<span v-else>{{ dado[coluna.key] }}</span>
						</td>
						<td />
					</tr>
				</tbody>

				<!-- Mensagem quando não houver dados -->
				<tbody v-else>
					<tr>
						<td
							colspan="100%"
							class="text-center py-4 text-gray-500"
						>
							<div class="flex flex-col items-center">
								<Icon
									icon="mdi:magnify-remove-outline"
									class="text-3xl mb-2"
								/>
								<p class="font-medium">
									{{ $t('nenhumFiltroSelecionado') }}
								</p>
								<p class="text-sm">
									{{ $t('selecioneOsCamposObrigatorios') }}
								</p>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div
			v-if="paginatedData.length > 0"
			class="container__pagination flex w-full content-between p-2 bg-white border-t border-gray-light"
		>
			<span class="flex items-center"
				>{{ $t('exibindo') }} {{ quantityPerPage * atualPage - (quantityPerPage - 1) }} -
				{{
					quantityPerPage * atualPage > totalRecords ? totalRecords : quantityPerPage * atualPage
				}}
				registros</span
			>
			<div class="container__pagination__pageinfo gap-1">
				<div class="flex">
					<label>{{ $t('exibir') }}</label>
					<select
						class="container__pagination__pageinfo__select"
						:value="quantityPerPage"
						@change="onSelectChange($event)"
					>
						<option
							v-for="(quantity, key) in quantityPerPageOptions"
							:key="key"
							:value="quantity"
						>
							{{ quantity }}
						</option>
					</select>
					<label> de {{ totalRecords }} registros</label>
				</div>
				<div class="container__pagination__pages">
					<i
						:class="[
							'fas fa-arrow-left container__pagination__elements__arrow',
							{ 'container__pagination__elements__arrow--disablled ': atualPage == 1 },
						]"
						@click="onPreviousPage"
					/>
					<label>{{ atualPage }}</label>
					<i
						:class="[
							'fas fa-arrow-right container__pagination__elements__arrow',
							{ 'container__pagination__elements__arrow--disablled ': atualPage == maxPages },
						]"
						@click="onNextPage"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
th:first-child {
	/* border-top-left-radius: 5px; */
}

th:last-child {
	/* border-top-right-radius: 5px; */
}

table {
	table-layout: auto;
	width: 100%;
	border-collapse: collapse;
}
.overflow-x-auto {
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}
.custom-border {
	border: 1px solid #737576;
	transition: border-color 0.3s ease;
	border-radius: 18px;
}

.custom-border:focus {
	border-color: #737576;
}

th {
	text-align: center;
}

th div {
	justify-content: center;
}
</style>
